import React from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';
import * as serializer from 'basic-editor-value-serializer';
import BaseElement from '../base';
import { determineBaseProperties } from '../utilities';
import IconSVG from '../icon/svg';
import controls from '../../node-menu/control-types';
import { appearanceRules } from './styles';
import { useEditorScroll } from './scroll';
import { brandStateRules } from './brand-states';
import Placeholder from './placeholder';
import './index.scss';

// Only the first header of a page sticks and changes as the page scrolls.
function isPrimary(editor, element) {
  let path = null;
  try {
    path = ReactEditor.findPath(editor, element);
  } catch (exception) {
    return false;
  }
  for (let index = 0; index < editor.children.length; index++) {
    if (editor.children[index].type === 'site-nav') {
      return path.length === 1 && index === path[0];
    }
  }
  return false;
}

function buildClassName(value, primary, state) {
  let className = 'site-nav-surface site-nav-editing';
  if (value.utility === true) {
    className = className + ' site-nav-show-utility';
  }
  if (primary === true && value.positioning === 'sticky') {
    className = className + ' site-nav-editor-sticky';
  }
  if (primary === true && value.overlay === true) {
    className = className + ' site-nav-editor-overlay';
  }
  if (state.scrolled === true) {
    className = className + ' site-nav-editor-scrolled';
  }
  if (state.away === true) {
    className = className + ' site-nav-editor-away';
  }
  return className;
}

function SiteNav(props) {
  const editor = useSlateStatic();
  const value = serializer.utilities.node.siteNav.readSettings(props.element);
  const primary = isPrimary(editor, props.element);
  const state = useEditorScroll(editor, props.element, value, primary);
  const children = React.Children.toArray(props.children);
  const style = {};
  const rules = appearanceRules(value, state.scrolled, props.element.properties.id);
  for (const [key, name] of [['gap', 'gap'], ['contentWidth', 'content-width'], ['logoHeight', 'logo-height'], ['gutter', 'gutter'], ['offset', 'offset']]) {
    style['--site-nav-' + name] = value[key] + 'px';
  }
  const base = determineBaseProperties({ ...props, className: 'site-nav-node', customNodeControls: [controls.SITE_NAV_SETTINGS] });
  const main = [];
  let utility = null;
  for (let index = 0; index < children.length; index++) {
    if (props.element.children[index].properties.role === 'utility') {
      utility = children[index];
    } else {
      main.push(children[index]);
    }
  }
  return <BaseElement {...base}><div {...props.attributes} className={buildClassName(value, primary, state)} data-arrangement={value.arrangement} data-frame={value.frame} data-treatment={value.treatment} style={style}>
    <style contentEditable={false}>{rules}</style><div className="site-nav-editor-utility">{utility}</div><div className="site-nav-main">{main}</div>
  </div></BaseElement>;
}

export function Region(props) {
  const role = serializer.utilities.node.siteNav.role(props.element);
  const id = serializer.utilities.node.siteNav.regionId(props.element);
  let rules = serializer.utilities.node.siteNav.regionRules(props.element);
  if (role === 'brand') {
    rules = rules + brandStateRules(props.element, id);
  }
  return <div {...props.attributes} id={id} className={'site-nav-region site-nav-' + role} data-site-nav-role={role}>
    <style contentEditable={false}>{rules}</style><Placeholder element={props.element} />{props.children}
  </div>;
}

export function Item(props) {
  let icon = null;
  const value = serializer.utilities.node.icon.readSettings(props.element);
  if (value !== null) {
    icon = <IconSVG settings={value} attached />;
  }
  return <div {...props.attributes} className="site-nav-item">{icon}{props.children}</div>;
}

export default SiteNav;
